/**
 * FI Data Purge Page
 *
 * React implementation of the FI Data Purge management page.
 * Handles:
 * - Date range filtering with quick filters
 * - Sort options (newest, oldest, highest, lowest)
 * - Aggregation level toggle (daily/monthly)
 * - Statistics display (lifetime and selected range)
 * - Report generation (mock implementation)
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { PurgeFilters } from './PurgeFilters';
import { PurgeStatistics } from './PurgeStatistics';
import { PurgeTable } from './PurgeTable';
import { Toast } from '../../ui/Toast';
import { useErrorHandler } from '../../../hooks/useErrorHandler';
import { purgeAPI } from '../../../services/purge-api.service';
import {
  getDefaultDateRange,
  formatDateToISO,
  toTableRows,
  sortPurgeRows,
} from '../../../utils/purge-utils';
import type {
  FIDataPurgePageProps,
  PurgeFiltersState,
  PurgeDataRecord,
  PurgeTableRow,
  PurgeStatisticsResponse,
  DataPurgingStatsData,
} from '../../../types/purge';

const buildInitialFilters = (): PurgeFiltersState => {
  const { startDate, endDate } = getDefaultDateRange();
  return {
    dateRange: { startDate, endDate },
    sortBy: 'newest',
    quickFilter: 'last_6_months',
    aggregationLevel: 'month',
  };
};

// Create initial filters once to ensure consistency
const INITIAL_FILTERS = buildInitialFilters();

export const FIDataPurgePage: React.FC<FIDataPurgePageProps> = () => {
  const { errorMessage, handleError, clearError } = useErrorHandler();

  // State
  const [rawRecords, setRawRecords] = useState<PurgeDataRecord[]>([]);
  const [statistics, setStatistics] = useState<PurgeStatisticsResponse>({
    lifetime_total: 0,
    selected_range_total: 0,
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingLifetimeStats, setIsLoadingLifetimeStats] = useState<boolean>(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState<boolean>(false);

  // Filter state - use same initial object for both
  const [filters, setFilters] = useState<PurgeFiltersState>(INITIAL_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<PurgeFiltersState>(INITIAL_FILTERS);

  // Success message state
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Check if filters have unapplied changes
  const hasUnappliedChanges = useMemo(() => {
    return JSON.stringify(filters) !== JSON.stringify(appliedFilters);
  }, [filters, appliedFilters]);

  // Convert raw records to table rows and apply sorting
  const tableRows = useMemo<PurgeTableRow[]>(() => {
    const rows = toTableRows(rawRecords, appliedFilters.aggregationLevel);
    return sortPurgeRows(rows, appliedFilters.sortBy);
  }, [rawRecords, appliedFilters.aggregationLevel, appliedFilters.sortBy]);

  /**
   * Load purge summary data from API
   * Also updates selected_range_total from the summary
   */
  const loadPurgeData = useCallback(async () => {
    setIsLoading(true);
    clearError();

    try {
      const response = await purgeAPI.getPurgeSummary({
        period: appliedFilters.aggregationLevel,
        start_date: formatDateToISO(appliedFilters.dateRange.startDate),
        end_date: formatDateToISO(appliedFilters.dateRange.endDate),
      });

      // Set table data
      setRawRecords(response.data);

      // Update selected_range_total from summary
      setStatistics((prev) => ({
        ...prev,
        selected_range_total: response.summary.total_records_purged,
      }));
    } catch (error) {
      handleError(error, { component: 'FIDataPurgePage', action: 'fetchPurgeData' });
      setRawRecords([]);
    } finally {
      setIsLoading(false);
    }
  }, [appliedFilters, handleError, clearError]);

  /**
   * Load lifetime statistics from dedicated endpoint
   * This fetches the total purged count across all time
   */
  const loadLifetimeStatistics = useCallback(async () => {
    setIsLoadingLifetimeStats(true);

    try {
      const response: DataPurgingStatsData = await purgeAPI.getLifetimePurgeStats();

      // Update only the lifetime_total, keep selected_range_total unchanged
      setStatistics((prev) => ({
        ...prev,
        lifetime_total: response.deletedCount,
      }));
    } catch (error) {
      handleError(error, { component: 'FIDataPurgePage', action: 'fetchLifetimeStats' });
    } finally {
      setIsLoadingLifetimeStats(false);
    }
  }, [handleError]);

  /**
   * Load lifetime statistics once on component mount
   * Lifetime stats don't change with filters
   */
  useEffect(() => {
    void loadLifetimeStatistics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Load purge data when applied filters change
   * This also updates the selected_range_total
   */
  useEffect(() => {
    void loadPurgeData();
  }, [loadPurgeData]);

  /**
   * Handle filter changes (user interaction)
   */
  const handleFiltersChange = useCallback((changes: Partial<PurgeFiltersState>) => {
    setFilters((prev) => ({
      ...prev,
      ...changes,
    }));
  }, []);

  /**
   * Apply filters (user clicks Apply button)
   */
  const handleApplyFilters = useCallback(() => {
    console.log('[FIDataPurgePage] Applying filters:', filters.dateRange);

    // Validate 8-month max range
    const monthsDiff = moment(filters.dateRange.endDate).diff(
      moment(filters.dateRange.startDate),
      'months',
      true // decimal precision
    );

    console.log('[FIDataPurgePage] Months diff:', monthsDiff.toFixed(2));

    if (monthsDiff > 8) {
      console.log('[FIDataPurgePage] Range exceeds 8 months, showing error');
      handleError(
        new Error('Date range cannot exceed 8 months. Please select a shorter date range.'),
        { component: 'FIDataPurgePage', action: 'validateFilters' }
      );
      return;
    }

    setAppliedFilters(filters);
    setSuccessMessage(null);
    clearError();
  }, [filters, handleError, clearError]);

  /**
   * Download report for a specific period
   * Fetches presigned URL from backend and triggers download via hidden anchor
   */
  const handleGenerateReport = useCallback(
    async (row: PurgeTableRow) => {
      setIsGeneratingReport(true);
      setSuccessMessage(null);
      clearError();

      try {
        // Get presigned URL from backend
        const response = await purgeAPI.downloadReport(row.period);

        // Create hidden anchor element to trigger download
        const link = document.createElement('a');
        link.href = response.download_url;
        link.download = response.file_name || `purge-report-${row.period}.csv`;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Show success message
        setSuccessMessage(`Report downloaded for ${row.displayPeriod}`);

        console.log('[FIDataPurgePage] Report download initiated:', {
          period: row.period,
          fileName: response.file_name,
          expiresIn: response.expires_in,
        });
      } catch (error) {
        handleError(error, { component: 'FIDataPurgePage', action: 'downloadReport' });
      } finally {
        setIsGeneratingReport(false);
      }
    },
    [handleError, clearError]
  );

  return (
    <div className="h-full flex flex-col p-4 space-y-4">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-semibold text-text-primary tracking-wide">
          FI Data Purge
        </h1>
        <p className="text-sm text-text-secondary mt-1">
          Monitor data purge operations, view statistics, and generate purge reports.
        </p>
      </div>

      {/* Error Toast - Fixed position */}
      {errorMessage && (
        <div className="fixed top-20 right-4 max-w-md" style={{ zIndex: 9999 }}>
          <Toast
            message={errorMessage}
            variant="error"
            onClose={clearError}
            duration={5000}
          />
        </div>
      )}

      {/* Success Toast - Fixed position */}
      {successMessage && (
        <div className="fixed top-28 right-4 max-w-md" style={{ zIndex: 9999 }}>
          <Toast
            message={successMessage}
            variant="success"
            onClose={() => setSuccessMessage(null)}
            duration={5000}
          />
        </div>
      )}

      {/* Filters */}
      <PurgeFilters
        filters={filters}
        onFiltersChange={handleFiltersChange}
        onApplyFilters={handleApplyFilters}
        hasUnappliedChanges={hasUnappliedChanges}
      />

      {/* Statistics Cards */}
      <PurgeStatistics
        lifetimeTotal={statistics.lifetime_total}
        selectedRangeTotal={statistics.selected_range_total}
        isLoadingLifetime={isLoadingLifetimeStats}
        isLoadingSelectedRange={isLoading}
      />

      {/* Purge Table */}
      <PurgeTable
        data={tableRows}
        aggregationLevel={appliedFilters.aggregationLevel}
        onGenerateReport={handleGenerateReport}
        isLoading={isLoading}
        isGeneratingReport={isGeneratingReport}
      />
    </div>
  );
};
